import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';

import DecisionIQBlulb from 'enrollment/assets/DecIQLightbulb.svg';
import { useConfigurationQuery, useSubscriberQuery } from 'theSource/queries';
import {
  useBenefitCategoryQuery,
  useBenefitPlansQuery,
} from 'enrollment/queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { EstimatedOopExpenseDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { PsButtonTuner } from 'stencilSetup';
import { queryKeyGenerator } from 'theSource/utils';

interface DecisionIQFinancialBannerProps {
  isHsaPlan: boolean;
  orgBenefitId: number;
  planId: number;
  subscriberMax: number;
  numPayPeriods: number;
  setEeContributionPPP: Dispatch<SetStateAction<number>>;
  setEeContributionAnnual: Dispatch<SetStateAction<number>>;
}

export const DecisionIQFinancialBanner = ({
  isHsaPlan,
  orgBenefitId,
  subscriberMax,
  planId,
  numPayPeriods,
  setEeContributionPPP,
  setEeContributionAnnual,
}: DecisionIQFinancialBannerProps) => {
  const { t } = useTranslation('pages.financial');
  const contextLookup = useSelector(selectEnrollmentContext);
  let orgPlanYearId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearId = contextLookup === 'life_event' ? orgPlanYearId : 0;

  const { subscriber } = useSubscriberQuery();
  const { configuration } = useConfigurationQuery();
  const { data } = useBenefitCategoryQuery({
    key: queryKeyGenerator(`contribution-${contextLookup}`, orgPlanYearId),
    enrollmentContextType: contextLookup,
    enabled: contextLookup !== 'null' && contextLookup !== '',
    lumosApiHost: String(configuration?.apiHost),
    getParamCategory: false,
    includeEligibleDependentsData: true,
    orgPlanYearId: orgPlanYearId,
  });

  const medElectedId = data?.data.benefits.find(
    (benefit) => benefit.benefit.benefit_lookup_code === 'medical'
  )?.coverage?.req_org_plan_id;
  let firstName = subscriber?.data.first_name.value;
  firstName = firstName
    ? String(firstName).charAt(0).toUpperCase() + String(firstName).slice(1)
    : '';

  const { plansData } = useBenefitPlansQuery({
    orgBenefitId: orgBenefitId,
    enrollmentContextType: contextLookup,
    enabled: true,
    lumosApiHost: configuration?.apiHost,
    keepPreviousData: true,
    includeEligibleDependentsData: true,
  });

  const decisionIQEnabled =
    plansData?.data.recommendation?.recommendation_for_hsa_fsa;

  if (!decisionIQEnabled) {
    return null;
  }

  const scrollToSelect = () => {
    const div = document.getElementById(
      `auto-contribute-box-${planId}`
    ) as HTMLElement;
    const offsets = div.getBoundingClientRect();

    window.scrollTo(0, offsets.top + window.scrollY - 90);
  };

  let contributionData: EstimatedOopExpenseDto | undefined = undefined;
  if (medElectedId) {
    contributionData =
      plansData?.data.recommendation?.recommendation_for_hsa_fsa?.estimated_oop_expense.find(
        (plan) => plan.Planid === medElectedId
      );
  } else {
    const planID =
      plansData?.data.recommendation?.recommendation_for_hsa_fsa?.plan_id;
    contributionData =
      plansData?.data.recommendation?.recommendation_for_hsa_fsa?.estimated_oop_expense.find(
        (plan) => plan.Planid === planID
      );
  }

  const contributionLowValue = contributionData?.TotalOOPLow || 0;
  let contributionDisplay =
    (isHsaPlan
      ? contributionData?.TotalOOPAvg
      : contributionData?.TotalOOPHigh) || 0;

  const useMaxContribution = contributionDisplay > subscriberMax;

  contributionDisplay = useMaxContribution
    ? subscriberMax
    : contributionDisplay;

  const handleAutoContribute = () => {
    setEeContributionPPP(
      Math.floor((contributionDisplay / numPayPeriods) * 100) / 100 || 0
    );
    setEeContributionAnnual(contributionDisplay || 0);
    scrollToSelect();
  };

  return (
    <div
      className="banner decisioniq mt-3"
      role="alert"
      id="decisionIQ-info-banner"
      data-testid="decisionIQ-info-banner"
    >
      <div className="mx-3 mx-md-4_5 my-4">
        <div className="d-inline-flex">
          <div className="decisioniq-hsa-icon">
            <img
              src={DecisionIQBlulb}
              alt="Decision IQ icon"
              className="mr-2"
              height="100%"
            />
          </div>
          <h4 className="decisioniq-hsa-header">
            {isHsaPlan ? t('decisionIQ.HSAtitle') : t('decisionIQ.FSAtitle')}
          </h4>
        </div>
        {firstName && (
          <p>
            {firstName}
            {t('decisionIQ.body1', {
              quantifier: isHsaPlan
                ? t('decisionIQ.atLeast')
                : t('decisionIQ.between'),
            })}
            {!isHsaPlan && (
              <>
                <span className="h3">${contributionLowValue}</span>
                {t('decisionIQ.and')}
              </>
            )}
            <span className="h3">${contributionDisplay}</span>
            {t('decisionIQ.body2', { planType: isHsaPlan ? 'HSA' : 'FSA' })}
          </p>
        )}
        {isHsaPlan ? (
          <p>{t('decisionIQ.body3')}</p>
        ) : (
          <p>
            <span className="semiBold">
              {t('decisionIQ.fsaDisclaimerTitle')}
            </span>{' '}
            {t('decisionIQ.fsaDisclaimer')}
          </p>
        )}
        <a
          href="https://www.irs.gov/publications/p502"
          className="btn btn-link show-underline ml-n3"
          target="_blank"
          rel="noreferrer"
        >
          {t('decisionIQ.govLink')}
        </a>
        {isHsaPlan && (
          <div id={`auto-contribute-box-${planId}`}>
            {/* text changes between mobile and tablet so two buttons to handle the change. First is desktop second is mobile */}
            <PsButtonTuner
              buttonId="contribute-suggested-amount"
              type="button"
              label={t('decisionIQ.contributeBtn')}
              level="secondary"
              extraClass="mt-3_5 btn-transparent-bg"
              data-testid="contribute-suggested-amount"
              onClick={handleAutoContribute}
            />
            <PsButtonTuner
              buttonId="contribute-suggested-amount-mobile"
              type="button"
              label={t('decisionIQ.mobileBtn')}
              level="secondary"
              extraClass="mt-3_5 btn-transparent-bg"
              data-testid="contribute-suggested-amount-mobile"
              onClick={handleAutoContribute}
            />
          </div>
        )}
      </div>
    </div>
  );
};
